import { Component, OnInit, Input, Output, EventEmitter, Injectable } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { NgbActiveModal, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { AlertModal } from 'src/app/app.model';

import { ClientService } from 'src/app/services/client.service';
import { AlertModalComponent } from '../alert-modal/alert-modal.component';
import { AuthenticationService } from 'src/app/services/authentication.service';
import { DataSharedService } from '../../services/data-shared.service';


interface Alert {
	type: string;
	message: string;
	alert_class?: string;
}

@Component({
	selector: 'app-edit-client',
	templateUrl: './edit-client.component.html',
	styleUrls: ['./edit-client.component.css']
})

@Injectable({
	providedIn: 'root',
})

export class EditClientComponent implements OnInit {

	alerts: Alert[] = [];

	@Input() public client: any;

	@Output() deactivateEditClientContactModal: EventEmitter<any> = new EventEmitter();

	error!: string;
	loading: boolean = false;

	formValid: boolean = false;
	clientForm: FormGroup | any;

	clientUuid: any;
	email_type: number = 0;
	frequency: number = 1;
	day_to_receive: number = 1;
	time_to_receive: string = '17:00';
	clientformValid: boolean = false;
	paymentTerms: any = {};
	invoiceEmail: string = '';
	isVatApplicable: boolean;

	activeSubpanel: 'details' | 'contacts' = 'details';
	openContacts: Set<number> = new Set<number>();

	client_contacts: any[] = [];

	constructor(
		private clientService: ClientService,
		public authService: AuthenticationService,
		public dataSharedService: DataSharedService,
		private modalService: NgbModal,
		private activeModalService: NgbActiveModal,
	) { }

	ngOnInit(): void {
		if (this.client?.uuid) {
			this.clientUuid = this.client.uuid;
		}
		this.clientForm = new FormGroup({
			name: new FormControl(this.client?.name, Validators.required),
			identifier: new FormControl(this.client?.identifier, []),
			address_1: new FormControl(this.client?.address?.address_1, []),
			address_2: new FormControl(this.client?.address?.address_2, []),
			post_zip_code: new FormControl(this.client?.address?.post_zip_code, []),
			country: new FormControl(this.client?.address?.country, []),
			payment_terms: new FormControl(this.client?.payment_terms, []),
			is_vat_applicable: new FormControl(this.client?.is_vat_applicable, []),
			client_company_number: new FormControl(this.client?.client_company_number, []),
			currency_format: new FormControl(this.client?.currency_format, []),
			client_reference: new FormControl(this.client?.client_reference, []),
			invoice_email: new FormControl(this.client?.invoice_email || '', []),
		});
		this.paymentTerms = this.client?.payment_terms;
		this.invoiceEmail = this.client?.invoice_email;
		this.isVatApplicable = this.client?.is_vat_applicable;
		this.getClient(this.client);
	}

	private makeEmptyContact(): any {
		return {
			uuid: this.client_contacts.length + 1,
			first_name: '',
			last_name: '',
			email: '',
			send_invite: true,
			timesheet_notification_settings: {
				email_type: this.email_type,
				frequency: this.frequency,
				day: this.day_to_receive,
				time: this.time_to_receive,
			}
		};
	}

	updateData(data: any): void {
		if (data?.client) {
			this.client = data.client;
			this.getClient(this.client);
		}
		this.loading = false;
	}

	getClient(client: any) {
		if (client?.contacts?.length > 0) {
			client.contacts.forEach((data: any) => {
				data.timesheet_notification_settings = data?.timesheet_notification_settings || {};
				if (data.timesheet_notification_settings.email_type == null) {
					data.timesheet_notification_settings.email_type = this.email_type;
					data.timesheet_notification_settings.day = this.day_to_receive;
					data.timesheet_notification_settings.frequency = this.frequency;
					data.timesheet_notification_settings.time = this.time_to_receive;
				}
			});
			this.client_contacts = client.contacts;
		} else {
			this.client_contacts = [this.makeEmptyContact()];
			this.openContacts.add(0);
		}
		this.ClientContactValidation(null);
		this.clientValidation(null);
	}

	setSubpanel(panel: 'details' | 'contacts') {
		this.activeSubpanel = panel;
	}

	setVat(v: number) {
		this.clientForm.get('is_vat_applicable')?.setValue(v);
	}

	toggleContact(i: number) {
		if (this.openContacts.has(i)) {
			this.openContacts.delete(i);
		} else {
			this.openContacts.add(i);
		}
	}

	addContact() {
		this.client_contacts.push(this.makeEmptyContact());
		const newIdx = this.client_contacts.length - 1;
		this.openContacts.clear();
		this.openContacts.add(newIdx);
		this.ClientContactValidation(null);
	}

	removeContact(i: number, evt?: Event) {
		if (evt) evt.stopPropagation();
		const target = this.client_contacts[i];
		const modalRef = this.modalService.open(AlertModalComponent, {
			size: 'md',
			backdrop: false,
			modalDialogClass: 'alert-modal modal-dialog-centered',
		});
		const data: AlertModal = {
			message: 'Are you sure you want to remove this contact?',
			yesAction: 'Remove',
			yesActionColor: '#0566EA',
			noAction: 'Cancel',
			noActionColor: 'transparent',
		};
		modalRef.componentInstance.data = data;
		modalRef.result.then((res) => {
			if (res?.action === 'yes') {
				const idx = this.client_contacts.indexOf(target);
				if (idx >= 0) {
					this.client_contacts.splice(idx, 1);
					const rebuilt = new Set<number>();
					this.openContacts.forEach(o => {
						if (o < idx) rebuilt.add(o);
						else if (o > idx) rebuilt.add(o - 1);
					});
					this.openContacts = rebuilt;
				}
				this.error = '';
				this.ClientContactValidation(null);
			}
		});
	}

	setApprovalType(contact: any, type: number) {
		contact.timesheet_notification_settings = contact.timesheet_notification_settings || {};
		contact.timesheet_notification_settings.email_type = type;
	}

	getInitials(contact: any): string {
		const f = (contact?.first_name || '').trim();
		const l = (contact?.last_name || '').trim();
		const initials = (f.charAt(0) + l.charAt(0)).toUpperCase();
		return initials || '?';
	}

	dayName(d: number | null | undefined): string {
		if (d == null) return '—';
		return ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][d] || '—';
	}

	onClose() {
		const modalRef = this.modalService.open(AlertModalComponent, {
			size: 'md',
			backdrop: false,
			modalDialogClass: 'alert-modal modal-dialog-centered',
		});

		let data: AlertModal = {
			message: 'Are you sure you want to discard Client Updation?',
			yesAction: 'Discard',
			noAction: 'Cancel',
			yesActionColor: '#0566EA',
			noActionColor: 'transparent',
		}
		modalRef.componentInstance.data = data;
		modalRef.result.then((data) => {
			if (data?.action === 'yes') {
				this.activeModalService.close();
			}
		});
	}

	ValidateEmail(email: string) {
		let email_regexp = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;
		return email_regexp.test(email);
	}

	async ClientContactValidation(event: any) {
		this.error = '';
		let hasErr = false;

		if (this.client_contacts.length === 0) {
			hasErr = true;
		}

		for (const c of this.client_contacts) {
			if (!c.first_name || c.first_name.trim() === '') { hasErr = true; break; }
			if (!c.last_name || c.last_name.trim() === '') { hasErr = true; break; }
			if (!c.email || c.email.trim() === '') { hasErr = true; break; }
			if (!this.ValidateEmail(c.email.trim())) { hasErr = true; break; }
			if (c.timesheet_notification_settings?.email_type === 1) {
				const t = c.timesheet_notification_settings.time;
				if (!t || (typeof t === 'string' && t.trim() === '')) { hasErr = true; break; }
			}
		}

		this.clientformValid = !hasErr;
	}

	async clientValidation(event: any) {
		this.error = '';
		let hasErr = false;
		if (this.clientForm.controls.name.errors ||
			(this.clientForm.controls.name.value || '').trim() === '') {
			hasErr = true;
		}

		this.formValid = !hasErr;
	}

	onDeleteClient() {
		const modalRef = this.modalService.open(AlertModalComponent, {
			size: 'md',
			backdrop: false,
			modalDialogClass: 'alert-modal modal-dialog-centered',
		});
		const data: AlertModal = {
			message: 'Are you sure you want to deactivate this client?',
			yesAction: 'Deactivate',
			yesActionColor: '#0566EA',
			noAction: 'Cancel',
			noActionColor: 'transparent',
		};
		modalRef.componentInstance.data = data;
		modalRef.result.then((res) => {
			if (res?.action === 'yes') {
				this.loading = true;
				this.clientService.deactivateAccount(this.clientUuid).subscribe(() => {
					this.activeModalService.close({ refresh: true });
				}, (err) => {
					this.loading = false;
					this.error = 'Could not deactivate this client.';
				});
			}
		});
	}

	onSubmit() {
		this.loading = true;
		const client_uuid = this.clientUuid;

		let client_address = {
			'address_1': this.clientForm.value.address_1,
			'address_2': this.clientForm.value.address_2,
			'post_zip_code': this.clientForm.value.post_zip_code,
			'country': this.clientForm.value.country,
		};

		const enrichedContacts = this.client_contacts.map(c => ({
			...c,
			name: `${c.first_name || ''} ${c.last_name || ''}`.trim(),
		}));

		const payload = {
			client: this.clientForm.value,
			client_contacts: enrichedContacts,
			address: client_address
		};

		this.clientService.updateClient(client_uuid, payload).subscribe({
			next: (res) => {
				this.activeModalService.close({ refresh: true });
				this.loading = false;
			},
			error: (err) => {
				this.loading = false;
				this.error = "Please review the form and ensure all required fields are filled, email addresses are valid and unique, and contact/address details are complete.";
			}
		});
	}
}
